import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Phone, Mail, MapPin, Globe, Shield, Smartphone, Camera, Code, Palette, Database, ArrowLeft, CheckCircle } from "lucide-react";
import { Link } from "react-router-dom";

const Services = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      {/* Navigation */}
      <nav className="bg-white/90 backdrop-blur-sm border-b border-blue-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <Shield className="h-8 w-8 text-blue-600" />
              <span className="text-2xl font-bold text-gray-900">Fremont Tech</span>
              <span className="text-sm text-gray-500">프리몬테크</span>
            </Link>
            <div className="hidden md:flex space-x-8">
              <Link to="/about" className="text-gray-700 hover:text-blue-600 transition-colors">ABOUT US</Link>
              <Link to="/services" className="text-blue-600 font-semibold">WHAT WE DO</Link>
              <Link to="/contact" className="text-gray-700 hover:text-blue-600 transition-colors">CONTACT US</Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <Link to="/" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-8">
          <ArrowLeft className="h-4 w-4 mr-2" />
          홈으로 돌아가기
        </Link>
      </div>

      {/* Hero Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <Badge className="mb-4 bg-blue-100 text-blue-800 hover:bg-blue-200">
            Our Services
          </Badge>
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            <span className="text-blue-600">전문적인</span> 서비스로 성공을 만들어갑니다
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            웹사이트 제작부터 앱 개발, CCTV 설치까지<br />
            고객의 모든 디지털 니즈를 원스톱으로 해결합니다
          </p>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Website Development */}
            <Card className="p-8 hover:shadow-xl transition-shadow border-blue-100">
              <CardHeader className="text-center pb-6">
                <div className="bg-blue-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                  <Globe className="h-10 w-10 text-blue-600" />
                </div>
                <CardTitle className="text-2xl text-blue-600 mb-2">웹사이트 제작</CardTitle>
                <CardDescription className="text-lg">
                  반응형 웹사이트부터 복잡한 웹 애플리케이션까지
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-900">반응형 디자인</h4>
                      <p className="text-sm text-gray-600">모든 디바이스에서 완벽한 사용자 경험</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-900">SEO 최적화</h4>
                      <p className="text-sm text-gray-600">검색엔진 상위 노출을 위한 최적화</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-900">사용자 중심 UX</h4>
                      <p className="text-sm text-gray-600">직관적이고 편리한 사용자 인터페이스</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-900">유지보수 지원</h4>
                      <p className="text-sm text-gray-600">지속적인 업데이트와 기술 지원</p>
                    </div>
                  </div>
                </div>
                <div className="mt-6 pt-6 border-t border-gray-100">
                  <h5 className="font-semibold text-gray-900 mb-2">기술 스택</h5>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="secondary">React</Badge>
                    <Badge variant="secondary">Vue.js</Badge>
                    <Badge variant="secondary">Node.js</Badge>
                    <Badge variant="secondary">PHP</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* App Development */}
            <Card className="p-8 hover:shadow-xl transition-shadow border-blue-100">
              <CardHeader className="text-center pb-6">
                <div className="bg-blue-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                  <Smartphone className="h-10 w-10 text-blue-600" />
                </div>
                <CardTitle className="text-2xl text-blue-600 mb-2">앱 개발</CardTitle>
                <CardDescription className="text-lg">
                  iOS, Android 네이티브 앱 및 크로스 플랫폼 앱
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-900">네이티브 앱 개발</h4>
                      <p className="text-sm text-gray-600">iOS, Android 플랫폼별 최적화</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-900">크로스 플랫폼</h4>
                      <p className="text-sm text-gray-600">React Native, Flutter 활용</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-900">UI/UX 디자인</h4>
                      <p className="text-sm text-gray-600">사용자 친화적인 앱 인터페이스</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-900">앱스토어 배포</h4>
                      <p className="text-sm text-gray-600">앱스토어 등록 및 관리 지원</p>
                    </div>
                  </div>
                </div>
                <div className="mt-6 pt-6 border-t border-gray-100">
                  <h5 className="font-semibold text-gray-900 mb-2">개발 플랫폼</h5>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="secondary">iOS</Badge>
                    <Badge variant="secondary">Android</Badge>
                    <Badge variant="secondary">React Native</Badge>
                    <Badge variant="secondary">Flutter</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* CCTV Services */}
            <Card className="p-8 hover:shadow-xl transition-shadow border-blue-100">
              <CardHeader className="text-center pb-6">
                <div className="bg-blue-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                  <Camera className="h-10 w-10 text-blue-600" />
                </div>
                <CardTitle className="text-2xl text-blue-600 mb-2">CCTV 상담 및 설치</CardTitle>
                <CardDescription className="text-lg">
                  보안 카메라 시스템 전문 컨설팅 및 설치
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-900">네트워크 카메라 시스템</h4>
                      <p className="text-sm text-gray-600">최신 IP 카메라 기술 적용</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-900">무료 컨설팅</h4>
                      <p className="text-sm text-gray-600">설치 환경에 적합한 솔루션 제안</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-900">전지역 방문 설치</h4>
                      <p className="text-sm text-gray-600">전국 어디든 직접 방문 설치</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-900">사후 관리 서비스</h4>
                      <p className="text-sm text-gray-600">지속적인 유지보수와 기술지원</p>
                    </div>
                  </div>
                </div>
                <div className="mt-6 pt-6 border-t border-gray-100">
                  <h5 className="font-semibold text-gray-900 mb-2">전문 분야</h5>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="secondary">IP 카메라</Badge>
                    <Badge variant="secondary">NVR 시스템</Badge>
                    <Badge variant="secondary">원격 모니터링</Badge>
                    <Badge variant="secondary">보안 솔루션</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">프로젝트 진행 과정</h2>
            <p className="text-xl text-gray-600">체계적이고 투명한 프로젝트 관리</p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-blue-600 text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                1
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">상담 및 분석</h3>
              <p className="text-gray-600 text-sm">
                고객의 요구사항을 정확히 파악하고 최적의 솔루션을 제안합니다
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-blue-600 text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                2
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">기획 및 설계</h3>
              <p className="text-gray-600 text-sm">
                사용자 중심의 설계와 체계적인 프로젝트 계획을 수립합니다
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-blue-600 text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                3
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">개발 및 구현</h3>
              <p className="text-gray-600 text-sm">
                최신 기술과 검증된 방법론으로 안정적인 솔루션을 구현합니다
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-blue-600 text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                4
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">배포 및 지원</h3>
              <p className="text-gray-600 text-sm">
                안전한 배포와 지속적인 기술 지원을 제공합니다
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">추가 서비스</h2>
            <p className="text-xl text-gray-600">더 나은 서비스를 위한 부가 솔루션</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="text-center p-6 hover:shadow-lg transition-shadow">
              <Code className="h-12 w-12 text-blue-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">시스템 통합</h3>
              <p className="text-gray-600 text-sm">
                기존 시스템과의 원활한 연동 및 통합 솔루션
              </p>
            </Card>
            
            <Card className="text-center p-6 hover:shadow-lg transition-shadow">
              <Database className="h-12 w-12 text-blue-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">데이터베이스 관리</h3>
              <p className="text-gray-600 text-sm">
                안전하고 효율적인 데이터베이스 설계 및 관리
              </p>
            </Card>
            
            <Card className="text-center p-6 hover:shadow-lg transition-shadow">
              <Palette className="h-12 w-12 text-blue-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">브랜딩 디자인</h3>
              <p className="text-gray-600 text-sm">
                브랜드 아이덴티티를 반영한 디자인 솔루션
              </p>
            </Card>
            
            <Card className="text-center p-6 hover:shadow-lg transition-shadow">
              <Shield className="h-12 w-12 text-blue-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">보안 컨설팅</h3>
              <p className="text-gray-600 text-sm">
                종합적인 보안 솔루션 및 컨설팅 서비스
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-blue-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            프로젝트를 시작할 준비가 되셨나요?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            무료 상담을 통해 귀하의 프로젝트에 대해 자세히 논의해보세요
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" variant="secondary" className="px-8 py-3">
              <Phone className="mr-2 h-5 w-5" />
              무료 상담 신청
            </Button>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-blue-600 px-8 py-3">
                연락처 보기
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <Link to="/" className="flex items-center space-x-2 mb-4">
                <Shield className="h-6 w-6 text-blue-400" />
                <span className="text-xl font-bold">Fremont Tech</span>
                <span className="text-sm text-gray-400">프리몬테크</span>
              </Link>
              <p className="text-gray-400 mb-4">
                신뢰할 수 있는 기술 파트너로서 고객의 성공을 위한<br />
                최고의 솔루션을 제공합니다.
              </p>
              <p className="text-sm text-gray-500">
                © 2024 Fremont Tech. All rights reserved.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">연락처</h3>
              <div className="space-y-2 text-gray-400">
                <div className="flex items-center space-x-2">
                  <Phone className="h-4 w-4" />
                  <span>010-2864-1942</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Mail className="h-4 w-4" />
                  <span>hyungwoo@naver.com</span>
                </div>
                <div className="flex items-center space-x-2">
                  <MapPin className="h-4 w-4" />
                  <span>대전 중구 대종로 550번길 5 유원오피스텔 1008호</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Globe className="h-4 w-4" />
                  <span>newvision.co.kr</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Services;